import { DayOfWeek, dayOfWeekValues } from '../../enums/DayOfWeek';
import { Availability } from './Availability';

/**
 * A class defines a time range at which an employee is available for one or more weekdays.
 */
export class GroupedAvailability {
  /** Start time of grouped availabilities. */
  startTime: string;
  /** End time of grouped availabilities. */
  endTime: string;
  /** Days of week who have the same start and end times. */
  dayOfWeeks: DayOfWeek[];

  private constructor(availability: Availability) {
    this.startTime = availability.startTime;
    this.endTime = availability.endTime;
    this.dayOfWeeks = [availability.dayOfWeek];
  }

  /**
   * Groups adjacent availabilities together, if they have the same time range.
   * @param availabilities availabilities for one week.
   */
  static from(availabilities: Availability[]): GroupedAvailability[] {
    const groupedAvailabilities: GroupedAvailability[] = [];
    let previousValue: GroupedAvailability;
    dayOfWeekValues().forEach(dayOfWeek => {
      const availability = availabilities.find(item => item.dayOfWeek === dayOfWeek);
      if (!availability) {
        return;
      }
      const groupedAvailability = new GroupedAvailability(availability);
      if (previousValue && previousValue.hasSameTimeRange(availability)) {
        previousValue.addDayOfWeek(dayOfWeek);
      } else {
        groupedAvailabilities.push(groupedAvailability);
        previousValue = groupedAvailability;
      }
    });
    return groupedAvailabilities;
  }

  /**
   * Returns true if start and end time matches the other availability.
   * @param availability the other availability which might be grouped.
   */
  private hasSameTimeRange(availability: Availability): boolean {
    return this.startTime === availability.startTime && this.endTime === availability.endTime;
  }

  /**
   * Adds day of week to this group. Day of week should be added only if start and end time matches.
   * @param dayOfWeek day of week to add to this group.
   */
  private addDayOfWeek(dayOfWeek: DayOfWeek) {
    this.dayOfWeeks.push(dayOfWeek);
  }
}
